/**
 * RecurringIssuesCard
 *
 * Athlete-facing counterpart to CommonWeaknessCard (coach-facing).
 * Surfaces body parts flagged by BiomechanicalObservation.body_part across the
 * athlete's recent evaluations so they can spot persistent patterns that a
 * per-eval narrative doesn't reveal.
 *
 * Data source: EvaluationSummaryItem.observation_types (Phase A, commit d374247).
 * No additional fetching — consumes the hydratedEvaluations array from AthleteDashboard.
 *
 * Three states:
 * 1. Insufficient data — fewer than MIN_EVALS_FOR_DETECTION evals with observation data
 * 2. No overlap — 3+ evals but every body part appears in only one session
 * 3. Populated — top TOP_ISSUES_LIMIT body parts with proportional bar
 */

import type { EvaluationSummaryItem } from '@/services/evaluationApi';

// ─── Constants ────────────────────────────────────────────────────────────────

/** Minimum number of evals with observation_types populated to attempt pattern detection. */
const MIN_EVALS_FOR_DETECTION = 3;

/** Maximum number of recurring issues to display. */
const TOP_ISSUES_LIMIT = 3;

/** How many recent evaluations to inspect (newest-first). */
const LOOKBACK_WINDOW = 10;

// ─── Props ────────────────────────────────────────────────────────────────────

interface RecurringIssuesCardProps {
  /** Evaluations sorted newest-first. Uses up to LOOKBACK_WINDOW most recent. */
  evaluations: EvaluationSummaryItem[];
}

// ─── Issue row ────────────────────────────────────────────────────────────────

interface IssueBarRowProps {
  label: string;
  sessionCount: number;
  maxSessionCount: number;
}

function IssueBarRow({ label, sessionCount, maxSessionCount }: IssueBarRowProps) {
  const pct = maxSessionCount > 0 ? (sessionCount / maxSessionCount) * 100 : 0;
  const sessionWord = sessionCount === 1 ? 'session' : 'sessions';

  return (
    <div style={{ marginBottom: '10px' }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          marginBottom: '4px',
        }}
      >
        <span
          style={{
            fontSize: '13px',
            fontWeight: 600,
            color: 'var(--color-text-primary)',
          }}
        >
          {label}
        </span>
        <span
          style={{
            fontSize: '11px',
            color: 'var(--color-text-muted)',
            whiteSpace: 'nowrap',
            marginLeft: '8px',
          }}
        >
          {sessionCount} {sessionWord}
        </span>
      </div>
      {/* Bar track */}
      <div
        style={{
          height: '6px',
          borderRadius: '3px',
          background: 'var(--color-border-subtle)',
          overflow: 'hidden',
        }}
      >
        {/* Fill — amber-tinted to signal an area needing attention */}
        <div
          data-testid={`issue-bar-${label}`}
          style={{
            height: '100%',
            width: `${pct}%`,
            borderRadius: '3px',
            background: 'var(--color-accent-gold, #F59E0B)',
            transition: 'width 0.4s ease',
          }}
        />
      </div>
    </div>
  );
}

// ─── Component ────────────────────────────────────────────────────────────────

export function RecurringIssuesCard({ evaluations }: RecurringIssuesCardProps) {
  // Take the most recent LOOKBACK_WINDOW evals (already sorted newest-first by caller)
  const window = evaluations.slice(0, LOOKBACK_WINDOW);

  // Count how many distinct evals flagged each body part
  const sessionCountByPart = new Map<string, number>();

  let evalsWithData = 0;
  for (const evalItem of window) {
    const types = evalItem.observation_types;
    if (!types || types.length === 0) continue;

    evalsWithData += 1;
    // Track unique body parts per eval to avoid double-counting within one session
    const seenInThisEval = new Set<string>();
    for (const part of types) {
      if (seenInThisEval.has(part)) continue;
      seenInThisEval.add(part);
      sessionCountByPart.set(part, (sessionCountByPart.get(part) ?? 0) + 1);
    }
  }

  // ── Insufficient data ──
  if (evalsWithData < MIN_EVALS_FOR_DETECTION) {
    return (
      <div
        style={{
          background: 'var(--color-dark-card)',
          borderRadius: '12px',
          border: '1px solid var(--color-border-subtle)',
          padding: '20px',
        }}
      >
        <p
          style={{
            fontSize: '14px',
            fontWeight: 700,
            color: 'var(--color-text-primary)',
            marginBottom: '8px',
          }}
        >
          Persistent Issues
        </p>
        <p
          style={{
            fontSize: '12px',
            color: 'var(--color-text-muted)',
            lineHeight: 1.6,
          }}
        >
          Need at least {MIN_EVALS_FOR_DETECTION} evaluations with observation data to detect
          patterns. Keep training!
        </p>
      </div>
    );
  }

  // Sort descending by session count, take top issues
  const sorted = Array.from(sessionCountByPart.entries()).sort((a, b) => b[1] - a[1]);

  // ── No recurring overlap ──
  // If every body part appears in only 1 session, there's no cross-session pattern
  const hasOverlap = sorted.some(([, count]) => count > 1);
  if (!hasOverlap) {
    return (
      <div
        style={{
          background: 'var(--color-dark-card)',
          borderRadius: '12px',
          border: '1px solid var(--color-border-subtle)',
          padding: '20px',
        }}
      >
        <p
          style={{
            fontSize: '14px',
            fontWeight: 700,
            color: 'var(--color-text-primary)',
            marginBottom: '8px',
          }}
        >
          Persistent Issues
        </p>
        <p
          style={{
            fontSize: '12px',
            color: 'var(--color-text-muted)',
            lineHeight: 1.6,
          }}
        >
          No recurring issues detected — your form has been varied.
        </p>
      </div>
    );
  }

  // ── Populated ──
  const topIssues = sorted.slice(0, TOP_ISSUES_LIMIT);
  const maxSessionCount = topIssues[0][1];

  return (
    <div
      style={{
        background: 'var(--color-dark-card)',
        borderRadius: '12px',
        border: '1px solid var(--color-border-subtle)',
        padding: '20px',
      }}
    >
      {/* Header */}
      <div style={{ marginBottom: '16px' }}>
        <p
          style={{
            fontSize: '14px',
            fontWeight: 700,
            color: 'var(--color-text-primary)',
            marginBottom: '2px',
          }}
        >
          Persistent Issues
        </p>
        <p
          style={{
            fontSize: '11px',
            color: 'var(--color-text-muted)',
          }}
        >
          Body parts flagged across your last {evalsWithData} scored session
          {evalsWithData !== 1 ? 's' : ''}
        </p>
      </div>

      {/* Issue bars */}
      {topIssues.map(([label, count]) => (
        <IssueBarRow
          key={label}
          label={label}
          sessionCount={count}
          maxSessionCount={maxSessionCount}
        />
      ))}
    </div>
  );
}
